import type { Vector3 } from "@babylonjs/core/Maths/math.vector";

export const CHIP_SOUND_IDS = [
  "rifle_fire",
  "door_open",
  "door_close",
  "pistol_fire",
  "shotgun_fire",
  "shotgun_pump",
  "weapon_reload",
  "dry_fire",
  "enemy_alert",
  "enemy_attack",
  "enemy_pain",
  "enemy_death_guard",
  "enemy_death_stalker",
  "player_hurt",
  "pickup_health",
  "pickup_ammo",
  "pickup_weapon",
] as const;

export type ChipSoundId = (typeof CHIP_SOUND_IDS)[number];

const CHIP_SOUND_SET = new Set<string>(CHIP_SOUND_IDS);
const SILENCE = 0.0001;

export function isChipSoundId(id: string): id is ChipSoundId {
  return CHIP_SOUND_SET.has(id);
}

interface VoiceOutput {
  readonly input: GainNode;
  readonly dispose: () => void;
}

interface ToneLayer {
  readonly offset: number;
  readonly duration: number;
  readonly frequencies: readonly number[];
  readonly volume: number;
  readonly waveform: OscillatorType;
  readonly attack?: number;
  readonly vibratoRate?: number;
  readonly vibratoDepth?: number;
}

interface NoiseLayer {
  readonly offset: number;
  readonly duration: number;
  readonly volume: number;
  readonly playbackRate: number;
  readonly filter: BiquadFilterType;
  readonly filterStart: number;
  readonly filterEnd?: number;
  readonly resonance?: number;
  readonly attack?: number;
}

export class ChipSoundSynthesizer {
  private readonly noiseBuffer: AudioBuffer;

  public constructor(
    private readonly context: AudioContext,
    private readonly effectsGain: GainNode,
  ) {
    this.noiseBuffer = this.createNoiseBuffer();
  }

  public play(id: ChipSoundId, position?: Vector3): number {
    const output = this.createOutput(position);
    const duration = this.schedule(id, output.input);
    globalThis.setTimeout(output.dispose, (duration + 0.1) * 1000);
    return duration;
  }

  private schedule(id: ChipSoundId, output: AudioNode): number {
    switch (id) {
      case "rifle_fire":
        this.tone(output, {
          offset: 0,
          duration: 0.09,
          frequencies: [190, 95, 42],
          volume: 0.16,
          waveform: "square",
        });
        this.noise(output, {
          offset: 0,
          duration: 0.1,
          volume: 0.23,
          playbackRate: 1.5,
          filter: "bandpass",
          filterStart: 2100,
          filterEnd: 700,
        });
        return 0.12;
      case "door_open":
        return this.scheduleDoor(output, false);
      case "door_close":
        return this.scheduleDoor(output, true);
      case "pistol_fire":
        return this.schedulePistolShot(output);
      case "shotgun_fire":
        return this.scheduleShotgunBlast(output);
      case "shotgun_pump":
        return this.scheduleShotgunPump(output);
      case "weapon_reload":
        this.scheduleMechanicalClack(output, 0, 0.7);
        this.scheduleMechanicalClack(output, 0.2, 1);
        this.tone(output, {
          offset: 0.08,
          duration: 0.18,
          frequencies: [120, 170],
          volume: 0.055,
          waveform: "square",
        });
        return 0.27;
      case "dry_fire":
        this.noise(output, {
          offset: 0,
          duration: 0.025,
          volume: 0.11,
          playbackRate: 2.2,
          filter: "highpass",
          filterStart: 1700,
        });
        this.tone(output, {
          offset: 0.018,
          duration: 0.045,
          frequencies: [720, 330],
          volume: 0.07,
          waveform: "square",
        });
        return 0.07;
      case "enemy_alert":
        return this.scheduleMonsterRoar(output, 1);
      case "enemy_attack":
        return this.scheduleMonsterRoar(output, 0.68);
      case "enemy_pain":
        return this.scheduleMonsterPain(output);
      case "enemy_death_guard":
        return this.scheduleGuardDeath(output);
      case "enemy_death_stalker":
        return this.scheduleStalkerDeath(output);
      case "player_hurt":
        return this.schedulePlayerPain(output);
      case "pickup_health":
        this.tone(output, {
          offset: 0,
          duration: 0.19,
          frequencies: [330, 440, 660, 880],
          volume: 0.075,
          waveform: "square",
        });
        return 0.2;
      case "pickup_ammo":
        this.tone(output, {
          offset: 0,
          duration: 0.13,
          frequencies: [390, 585, 780],
          volume: 0.065,
          waveform: "square",
        });
        return 0.14;
      case "pickup_weapon":
        this.tone(output, {
          offset: 0,
          duration: 0.3,
          frequencies: [220, 330, 440, 660, 880],
          volume: 0.085,
          waveform: "square",
        });
        return 0.31;
    }
  }

  private schedulePistolShot(output: AudioNode): number {
    this.noise(output, {
      offset: 0,
      duration: 0.045,
      volume: 0.3,
      playbackRate: 2.1,
      filter: "highpass",
      filterStart: 1200,
      filterEnd: 2600,
    });
    this.noise(output, {
      offset: 0.008,
      duration: 0.12,
      volume: 0.16,
      playbackRate: 0.8,
      filter: "lowpass",
      filterStart: 1100,
      filterEnd: 320,
    });
    this.tone(output, {
      offset: 0,
      duration: 0.11,
      frequencies: [240, 135, 72, 48],
      volume: 0.22,
      waveform: "sawtooth",
      attack: 0.001,
    });
    return 0.14;
  }

  private scheduleShotgunBlast(output: AudioNode): number {
    this.noise(output, {
      offset: 0,
      duration: 0.055,
      volume: 0.36,
      playbackRate: 1.7,
      filter: "highpass",
      filterStart: 850,
      filterEnd: 2100,
    });
    this.noise(output, {
      offset: 0,
      duration: 0.29,
      volume: 0.32,
      playbackRate: 0.48,
      filter: "lowpass",
      filterStart: 1450,
      filterEnd: 180,
    });
    this.tone(output, {
      offset: 0,
      duration: 0.3,
      frequencies: [165, 105, 68, 43, 31],
      volume: 0.3,
      waveform: "sawtooth",
      attack: 0.001,
    });
    this.tone(output, {
      offset: 0.005,
      duration: 0.2,
      frequencies: [82, 61, 46, 34],
      volume: 0.16,
      waveform: "square",
      attack: 0.001,
    });
    return 0.32;
  }

  private scheduleShotgunPump(output: AudioNode): number {
    this.scheduleMechanicalClack(output, 0, 0.75);
    this.scheduleMechanicalClack(output, 0.1, 1);
    this.tone(output, {
      offset: 0.075,
      duration: 0.085,
      frequencies: [155, 96],
      volume: 0.07,
      waveform: "square",
    });
    return 0.2;
  }

  private scheduleMechanicalClack(output: AudioNode, offset: number, intensity: number): void {
    this.noise(output, {
      offset,
      duration: 0.035,
      volume: 0.15 * intensity,
      playbackRate: 2.3,
      filter: "highpass",
      filterStart: 1100,
    });
    this.tone(output, {
      offset,
      duration: 0.045,
      frequencies: [420, 165],
      volume: 0.08 * intensity,
      waveform: "square",
      attack: 0.001,
    });
  }

  private scheduleMonsterRoar(output: AudioNode, lengthScale: number): number {
    const duration = 0.58 * lengthScale;
    this.tone(output, {
      offset: 0,
      duration,
      frequencies: [105, 82, 119, 91, 68, 54],
      volume: 0.18,
      waveform: "sawtooth",
      attack: 0.018,
      vibratoRate: 24,
      vibratoDepth: 11,
    });
    this.tone(output, {
      offset: 0.015,
      duration: duration * 0.92,
      frequencies: [53, 42, 59, 45, 34, 28],
      volume: 0.11,
      waveform: "square",
      attack: 0.012,
      vibratoRate: 17,
      vibratoDepth: 5,
    });
    this.noise(output, {
      offset: 0.01,
      duration: duration * 0.94,
      volume: 0.13,
      playbackRate: 0.52,
      filter: "bandpass",
      filterStart: 620,
      filterEnd: 230,
      resonance: 2.4,
      attack: 0.025,
    });
    return duration + 0.02;
  }

  private scheduleDoor(output: AudioNode, closing: boolean): number {
    const duration = closing ? 0.74 : 0.64;
    this.noise(output, {
      offset: 0,
      duration: 0.055,
      volume: 0.19,
      playbackRate: 0.8,
      filter: "highpass",
      filterStart: 700,
    });
    this.tone(output, {
      offset: 0.025,
      duration: duration - 0.11,
      frequencies: closing ? [92, 86, 70, 55] : [54, 80, 96, 76],
      volume: 0.13,
      waveform: "sawtooth",
      attack: 0.035,
    });
    this.noise(output, {
      offset: 0.035,
      duration: duration - 0.12,
      volume: 0.22,
      playbackRate: 0.38,
      filter: "bandpass",
      filterStart: closing ? 720 : 350,
      filterEnd: closing ? 220 : 680,
      resonance: 1.2,
      attack: 0.03,
    });
    this.tone(output, {
      offset: duration - 0.1,
      duration: 0.15,
      frequencies: [85, 38, 24],
      volume: 0.19,
      waveform: "triangle",
    });
    this.noise(output, {
      offset: duration - 0.1,
      duration: 0.13,
      volume: 0.26,
      playbackRate: 0.6,
      filter: "lowpass",
      filterStart: 560,
      filterEnd: 160,
    });
    return duration + 0.07;
  }

  private scheduleMonsterPain(output: AudioNode): number {
    this.tone(output, {
      offset: 0,
      duration: 0.26,
      frequencies: [265, 196, 228, 142, 88],
      volume: 0.17,
      waveform: "sawtooth",
      attack: 0.008,
      vibratoRate: 29,
      vibratoDepth: 15,
    });
    this.noise(output, {
      offset: 0.01,
      duration: 0.21,
      volume: 0.1,
      playbackRate: 0.72,
      filter: "bandpass",
      filterStart: 980,
      filterEnd: 410,
      resonance: 1.8,
      attack: 0.012,
    });
    return 0.28;
  }

  private scheduleGuardDeath(output: AudioNode): number {
    this.tone(output, {
      offset: 0,
      duration: 0.54,
      frequencies: [180, 145, 116, 92, 67, 44],
      volume: 0.19,
      waveform: "sawtooth",
      attack: 0.012,
      vibratoRate: 21,
      vibratoDepth: 9,
    });
    this.tone(output, {
      offset: 0.08,
      duration: 0.45,
      frequencies: [90, 72, 58, 45, 31],
      volume: 0.1,
      waveform: "square",
      vibratoRate: 14,
      vibratoDepth: 4,
    });
    this.noise(output, {
      offset: 0.04,
      duration: 0.49,
      volume: 0.12,
      playbackRate: 0.48,
      filter: "bandpass",
      filterStart: 540,
      filterEnd: 160,
      resonance: 2.1,
      attack: 0.015,
    });
    return 0.56;
  }

  private scheduleStalkerDeath(output: AudioNode): number {
    this.tone(output, {
      offset: 0,
      duration: 0.8,
      frequencies: [132, 101, 122, 84, 63, 46, 31, 24],
      volume: 0.21,
      waveform: "sawtooth",
      attack: 0.018,
      vibratoRate: 18,
      vibratoDepth: 13,
    });
    this.tone(output, {
      offset: 0.05,
      duration: 0.73,
      frequencies: [66, 51, 61, 42, 31, 23],
      volume: 0.12,
      waveform: "square",
      attack: 0.012,
      vibratoRate: 11,
      vibratoDepth: 5,
    });
    this.noise(output, {
      offset: 0.03,
      duration: 0.74,
      volume: 0.14,
      playbackRate: 0.38,
      filter: "bandpass",
      filterStart: 430,
      filterEnd: 110,
      resonance: 2.8,
      attack: 0.02,
    });
    return 0.82;
  }

  private schedulePlayerPain(output: AudioNode): number {
    this.tone(output, {
      offset: 0,
      duration: 0.3,
      frequencies: [238, 184, 205, 151, 112],
      volume: 0.16,
      waveform: "sawtooth",
      attack: 0.006,
      vibratoRate: 25,
      vibratoDepth: 8,
    });
    this.tone(output, {
      offset: 0.015,
      duration: 0.27,
      frequencies: [119, 92, 103, 76, 56],
      volume: 0.07,
      waveform: "square",
      attack: 0.008,
    });
    this.noise(output, {
      offset: 0.018,
      duration: 0.2,
      volume: 0.075,
      playbackRate: 0.84,
      filter: "bandpass",
      filterStart: 1200,
      filterEnd: 520,
      resonance: 1.5,
      attack: 0.009,
    });
    return 0.32;
  }

  private tone(output: AudioNode, layer: ToneLayer): void {
    const start = this.context.currentTime + layer.offset;
    const end = start + layer.duration;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = layer.waveform;
    this.scheduleFrequencySteps(oscillator.frequency, layer.frequencies, start, layer.duration);
    this.scheduleEnvelope(gain.gain, start, end, layer.volume, layer.attack ?? 0.004);

    if (layer.vibratoRate && layer.vibratoDepth) {
      const vibrato = this.context.createOscillator();
      const vibratoGain = this.context.createGain();
      vibrato.frequency.value = layer.vibratoRate;
      vibratoGain.gain.value = layer.vibratoDepth;
      vibrato.connect(vibratoGain).connect(oscillator.frequency);
      vibrato.start(start);
      vibrato.stop(end + 0.01);
    }

    oscillator.connect(gain).connect(output);
    oscillator.start(start);
    oscillator.stop(end + 0.01);
  }

  private noise(output: AudioNode, layer: NoiseLayer): void {
    const start = this.context.currentTime + layer.offset;
    const end = start + layer.duration;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = this.noiseBuffer;
    source.playbackRate.value = layer.playbackRate;
    filter.type = layer.filter;
    filter.Q.value = layer.resonance ?? 0.7;
    filter.frequency.setValueAtTime(layer.filterStart, start);
    if (layer.filterEnd && layer.filterEnd !== layer.filterStart) {
      filter.frequency.exponentialRampToValueAtTime(layer.filterEnd, end);
    }
    this.scheduleEnvelope(gain.gain, start, end, layer.volume, layer.attack ?? 0.002);
    source.connect(filter).connect(gain).connect(output);
    source.start(start);
    source.stop(end);
  }

  private scheduleFrequencySteps(
    parameter: AudioParam,
    frequencies: readonly number[],
    start: number,
    duration: number,
  ): void {
    const divisor = Math.max(1, frequencies.length - 1);
    frequencies.forEach((frequency, index) => {
      parameter.setValueAtTime(frequency, start + (duration * index) / divisor);
    });
  }

  private scheduleEnvelope(
    parameter: AudioParam,
    start: number,
    end: number,
    volume: number,
    attack: number,
  ): void {
    parameter.setValueAtTime(SILENCE, start);
    parameter.linearRampToValueAtTime(volume, Math.min(end, start + attack));
    parameter.exponentialRampToValueAtTime(SILENCE, end);
  }

  private createOutput(position?: Vector3): VoiceOutput {
    const input = this.context.createGain();
    const compressor = this.context.createDynamicsCompressor();
    compressor.threshold.value = -15;
    compressor.knee.value = 8;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.001;
    compressor.release.value = 0.08;
    input.connect(compressor);

    if (!position) {
      compressor.connect(this.effectsGain);
      return {
        input,
        dispose: () => {
          input.disconnect();
          compressor.disconnect();
        },
      };
    }

    const panner = this.context.createPanner();
    panner.panningModel = "HRTF";
    panner.distanceModel = "inverse";
    panner.refDistance = 2;
    panner.maxDistance = 45;
    panner.rolloffFactor = 1.2;
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;
    compressor.connect(panner).connect(this.effectsGain);
    return {
      input,
      dispose: () => {
        input.disconnect();
        compressor.disconnect();
        panner.disconnect();
      },
    };
  }

  private createNoiseBuffer(): AudioBuffer {
    const length = Math.floor(this.context.sampleRate * 0.9);
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    let value = 0;
    for (let index = 0; index < length; index += 1) {
      if (index % 12 === 0) value = Math.random() * 2 - 1;
      data[index] = Math.round(value * 7) / 7;
    }
    return buffer;
  }
}
